/// Cody McCodeface, mocked in WebXR.
///
/// This is the demo on the website and the first bones of the WebXR client on the roadmap. It puts the headset
/// app's windows where the headset app puts them — a shallow arc at eye level, the input tools low and near, the
/// disk as blocks on a table — so you can walk around the product without owning a Vision Pro.
///
/// **It is a mock.** Nothing connects to opencode, no agent runs, and every word in the panels is written down
/// in `panels.js`. The real app is native Swift and talks to a stock `opencode web` over HTTP.
///
/// How the windows are drawn is one seam, `world` (Josh 10-01: phones were slow): Snap and Look are the page's own DOM
/// (core/render-dom.js — Snap one window per screen in a pinned row, Look the arc in CSS 3D turned by the phone), with
/// no WebGL and no three.js; an immersive session (VR on a Quest, AR on Android) loads the room (core/render-gl.js)
/// only once the session is granted, and gives the windows back to the DOM when it ends. Everything else here — the
/// Macs, the windows, sessions, streams, typing — is the same whichever draws them.
import { watchSendLogs } from './sendlogs/index.js'   // first: its console ring (sendlogs/buffer.js) hears the page from the start
import * as Panels from './core/panels/index.js'
import { savedConnection, forgetConnection, FetchTransport, servedByMac, savedRooms, ROOM_CAP } from './core/transport.js'
import { Client } from './core/client.js'
import { connectDirect, relayOnly, watchRoute } from './core/rtc.js'
import { phoneBridge } from './core/phonekey.js'
import { scheduleOf } from './core/schedulecard.js'
import { Macs, Mac } from './core/macs.js'
import { MacWindows } from './core/wire.js'
import { StreamKeys } from './core/streamkeys.js'
import { HomeWindow } from './windows/home/home.js'
import MacWindowChrome from './windows/macwindow/macwindow.js'
import { RobotsStore } from './tiny-engineer/roster.js'
import { rememberMacColor } from './tiny-engineer/maccolors.js'
import { Extensions, runAction } from './extensions/extensions.js'
import { watchLicense } from './license/index.js'
import { watchDevCode } from './devcode/index.js'
import { takeRelayGift, offerRelayGift } from './relaygift/index.js'
import { watchRelayStatus } from './core/relaystatus.js'
import { watchMacState } from './core/macstate.js'
import { watchMcp, pressMcp } from './core/mcp.js'
import { watchCanned } from './canned/index.js'
import { asksOf } from './core/homeasks.js'
import { appURL } from './core/assets.js'
import { wwwOrigin } from './core/site.js'
import { windowSpec, hasWindow, windowKinds, windowStyles, sessionOf } from './core/windows.js'
import { titleOf, retitle, clip, SHORT } from './core/session-title.js'
import { homeItem } from './core/home-items.js'
import './windows/index.js'   // every feature registers its windows, Home items and wire kinds
import { COLOR, SPACE3D, BLOCKS, COMPONENT, hex, tintFor } from './core/tokens.js'
import { arrange } from './core/arrange.js'
import { snapLayout, freeLayout } from './core/snap-layout.js'
import { logLine } from './core/log.js'
import { installXRButton } from './core/xr-button.js'
import { DomWorld } from './core/render-dom.js'
import { makeLive } from './core/live.js'
import { startMeter, getWantedFPS } from './core/meter.js'
import { applyAttention, MAX_STREAMS } from './core/stream-attention.js'
import { makePlayer, drawLockBanner } from './windows/macwindow/player.js'
import { readStamp, versionText as stampText, newerBuild } from './core/build-stamp.js'
import { airKeyEdit } from './core/air-keys.js'
import { collapseContinuations } from './windows/home/continuations.js'
import { startBuildTag } from './buildtag/index.js'
import { startOffline, checkVersion, offlineLinkUp, keepPrompt, rememberMac, prefetchMac, offlineHome, newerVersion, offlineClient, isOfflineClient, offlineLineFor, keptFor, eraseKept, retryKept, promptAhead, promptSent, promptCutOff, chooseKept, cancelInbox } from './offline/index.js'
import { userTurnsWith } from './offline/ahead.js'
import { isClaudeSession } from './offline/route.js'
import { answerAsk } from './claudeask/answer.js'
import { evenBridge, loadEven, hostFor, evenConnection } from './even/index.js'

// This build (Josh, 09-29: tell at a glance that it is the new one): `npm run sync` stamps the page with the time,
// the commit and when it was built (services/app/stamp.mjs). The time stays first in the text, as the older
// pages' reload check looks for it; the footer and the Connection section then show the commit and the build time.
const buildEl = document.getElementById('build')
const BUILD = readStamp(buildEl)   // core/build-stamp.js
const versionText = () => stampText(BUILD)
if (buildEl && (BUILD.commit || BUILD.builtAt)) buildEl.textContent = versionText()

const EYE = SPACE3D.eyeHeight     // where a standing person's eyes are, in metres — the app's own figure
const RADIUS = SPACE3D.arcRadius  // how far the arc of windows sits from you
const PPM = SPACE3D.pointsPerMetreAtOneMetre

/// A window's size and place, from design/tokens.json. The app sizes windows in POINTS and visionOS scales
/// them with distance (1360 points to the metre at one metre), so the metres drawn here are points / 1360 ×
/// how far away the window sits. The tokens are the app's own defaultSize values; nothing here is a guess
/// except `paneMultiplier`, which the token says is estimated.
function place(key, extra = {}) {
  const t = SPACE3D.windows[key]
  const distance = extra.radius ?? t.radius ?? RADIUS
  const scale = t.metres ? 1 : distance / PPM
  const wide = (t.paneMultiplier ?? 1)
  return { w: t.w * scale * wide, h: t.h * scale, angle: t.angle, lift: t.lift, radius: distance, ...extra }
}

/// Every window in the scene: what it is, how big, where on the arc, and the caption that explains it.
///
/// `angle` is degrees around you (negative is left), `lift` is metres above or below eye level. Sizes are in
/// metres, which is how visionOS thinks about windows too — a session window really is about a metre across.
/// The paired Macs (macs.js): each its own link, 'connecting' | 'online' | 'offline' (with why), as Home's
/// sections show them. `client` and `macWindows` below are the current Mac's — the one last chosen.
/// Inside the Even app (even/index.js, docs/flows/even-g2.yaml › One address): the glasses' add-on, loaded before the
/// Macs so they are the glasses' paired Macs, signing with this device's key. Anywhere else `even` is null, nothing is
/// awaited or loaded, and everything below is as it always was.
const even = evenBridge() ? await loadEven() : null
if (!even) addEventListener('load', () => {
  // a bridge that only appears once the page has loaded: start again once, now that it is there
  try { if (evenBridge() && !sessionStorage.getItem('cody.evenLate')) { sessionStorage.setItem('cody.evenLate', '1'); location.reload() } } catch {}
}, { once: true })
const macs = new Macs({
  site: location.origin,
  ...(even ? { rooms: () => even.rooms(), keyOnly: true } : {}),
  onChange: (mac, what) => { syncCurrent(); even?.macChanged(mac.key); if (what === 'forgotten') { even?.forget(mac.key); return } if (what === 'sessions' || what === 'transcript') rememberMac(mac); repaint() },
  onConnected: (mac) => macConnected(mac),
  onClosed: (mac) => { for (const x of [...streams]) if (x.macKey === mac.key) closeStream(x); if (mac.windows) mac.windows = null; even?.macChanged(mac.key); syncCurrent(); repaint() },
  onEvent: (mac, ev) => eventFrom(mac, ev),
  onStatus: (mac, text) => { liveStatus.textContent = (macs.several ? mac.name + ': ' : '') + text },
})
// a linked Mac learned from a `devices` answer (windows/devices) connects now, like any paired Mac
// — and one named again that its door refused (another invite there, maybe just given this one's) tries now
window.addEventListener('cody:macs-learned', (e) => { if (!live.hidden) { macs.adopt(); macs.retryRefused(e.detail?.named) } })
/// The Mac a window belongs to: the one it was opened on (spec.macKey), or the current one.
const macOf = (spec) => (spec?.macKey ? macs.get(spec.macKey) : macs.current)
const clientOf = (spec) => macOf(spec)?.client || null
const homeWin = new HomeWindow({
  open: (id, key) => openFromHome(id, key),
  openSession: (s, key) => openSession(s, key),
  deleteSessions: (items) => deleteSessions(items),
  forgetMac: (key) => forgetThisMac(key),
  pauseMac: (key) => { macs.pause(key); const m = macs.get(key); if (m) liveStatus.textContent = m.name + ' is paused — Resume from its ⋯ on Home.'; syncCurrent(); repaint() },
  resumeMac: (key) => { macs.resume(key); syncCurrent(); repaint() },
  // a refused list's card button (core/problemcard.js): ask the Mac again now, the waits start over
  problemAction: (key, act) => { macs.get(key)?.client?.retryRefused({ reset: true }).catch(() => {}); repaint() },
  // a Mac's tab picked: the app's current Mac — what every window and action opened from now on is for (home-mac-tabs.md › The tab is the Mac)
  chooseMac: (key) => macs.choose(key),
  relayAction: (what, key) => relayAction(what, key),
  startNow: (key) => startNow(key),
  // an MCP card's button (core/mcp.js): the Mac signs in (its browser), retries or restarts the workspace
  mcpPress: (key, id, action) => pressMcp(macs.get(key), id, action, repaint),
  // Home's sort, filters and Links Open On (the Mac's ⋯)
  get settings() { return SETTINGS },   // (declared further down)
  // Columns widen Home (a column per Mac, up to three side by side)
  resize: (w, h) => { const spec = WINDOWS.find((x) => x.id === 'home'); if (spec) resizeHtmlWindow(spec, w, h) },
  // an extension row's action (extensions.js): sent, and what the Mac's result names opened on that Mac
  extAction: (key, ext, req) => runAction({
    client: macs.get(key)?.client, open: (kind, arg) => openWindow(kind, arg, key), openMacWindow: (w) => openMacWindow(w, macs.get(key)),
    site: wwwOrigin(),   // the board (windows/influence): read from www, the account side
    note: (text) => { liveStatus.textContent = text },
  }, key, { ext, ...req }),
  // the Mac's pairing-from-anywhere card answered (Pair · Not now): over that Mac's devices wire (windows/meetpair)
  meetAnswer: (key, code, pair) => macs.get(key)?.windows?.send('devices', { t: 'meetAnswer', code, pair: !!pair }),
  // an extension's notice dismissed (extensions.js)
  extDismiss: (key, id) => Extensions.dismiss(key, id),
  // a Claude conversation's ask answered from Home's Needs you strip (claudeask/): over that Mac's glance
  claudeAsk: (key, ask, choice) => { const m = macs.get(key); answerAsk(m?.windows, key, ask, choice, { online: m?.link === 'online' }) },
})
/// A relay card's button (relaycard.js): the pairing flow (the Mac's link carries its invite — there is no door
/// page on app since the split), or try again. Coming back from the pairing page reloads the app, and every
/// Mac connects again.
function relayAction(what, key) {
  if (what === 'invite' || what === 'pair') { pairThisDevice(); return }
  // This Mac doesn't know this phone (rtc.js `phone-unpaired`): the app's own Add a Mac
  if (what === 'addMac') { pairThisDevice(); return }
  if (what === 'retry') { macs.retry(key); liveStatus.textContent = 'Asking Cody’s relay again…' }
  // Verify with passkey: the Mac asks for it on every new session; the prompt comes up and the Mac connects by itself
  if (what === 'verify') { macs.verify(key); liveStatus.textContent = phoneBridge() ? 'Confirm it’s you on this phone…' : 'Your passkey, please…' }
}
/// Start Now on a Mac's "Off until … (schedule)" (schedulecard.js): the Mac starts that workspace as if by hand,
/// until the schedule's next stop. Also Start / Restart on a workspace that is stopped (schedulecard.js). The room
/// stays — the Mac app holds it (docs/features/mac-room.md) — and its `cody.workspace` event reloads the sessions.
function startNow(key) {
  const m = macs.get(key), entry = scheduleOf(m)
  if (!m?.windows || !entry) return
  m.scheduleAsked = true; m.scheduleRefused = ''
  m.windows.scheduleStart(entry.id, 'A browser')
  liveStatus.textContent = `Asked ${m.name} to start “${entry.name}”…`
  repaint()
}
addEventListener('focus', () => macs.retryRelay())
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') macs.retryRelay() })

const WINDOWS = [
  {
    id: 'macapps', short: 'Mac windows', logical: 700, draw: Panels.macApps, ...place('mac', { radius: 2.0 }), px: 1000, closable: true,
    name: 'Mac windows',
  },
  {
    id: 'home', short: 'Home', logical: 620, dom: homeWin, ...place('home'), px: 1240,
    name: 'Home — your Macs and their sessions',
    blurb: 'Every paired Mac with its sessions. New Session in the toolbar; long-press a row and that Mac\'s list becomes a checklist with Select All and Delete N Sessions. Widgets, Mac Windows, Files on a Mac, Permissions, What\'s New, Progress, the Status Orb and Influence are all one tap away.',
  },
]

// MARK: the windows

/// Snap on a screen (not in a headset): the windows are pinned in Snap's row (core/snap-layout.js), not moved by hand.
const snapped = () => world?.mode === 'snap'
/// The arc laid out again after a window came or went (a stream opened or closed): every window moves to its place —
/// in Snap the fixed row, otherwise the free arrangement (with the poses placed by hand before Snap given back).
function relayout() {
  if (snapped()) snapLayout(WINDOWS, { RADIUS, isLive: (id) => LIVE_WINDOWS.has(id) })
  else { freeLayout(WINDOWS); arrange(WINDOWS, { RADIUS, isLive: (id) => LIVE_WINDOWS.has(id) }) }
  world?.layout()
  if (world?.current) world.go(world.current)
  world?.drawStrip()
}

/// A window painted by core/panels into a canvas of its own (the legacy painted windows): its canvas, made once and
/// painted whenever the live state changes (repaintLiveWindows); the world shows the canvas.
function ensureCanvas(spec) {
  if (spec.canvas || !spec.draw) return
  const canvas = document.createElement('canvas')
  canvas.width = spec.px
  canvas.height = Math.round((spec.px * spec.h) / spec.w)
  // the drawing code works in `logical` units and is scaled up to the canvas
  const context = canvas.getContext('2d')
  const scale = canvas.width / spec.logical
  context.scale(scale, scale)
  spec.draw(context, spec.logical, Math.round(canvas.height / scale))
  spec.canvas = canvas
  spec.context = context
  spec.scale = scale
}

// MARK: the headset's windows (windows/*.js, docs/wireframe/web-windows.md)
//
// Every window the headset has, built as HTML by windows/<module>.js and opened by kind — from Home's toolbar,
// a session's menus, another window. `many` windows can be open several times (keyed by their argument);
// the rest are one of a kind, as the headset's OnlyOne makes them.
/// What a registered window's key or title may ask (core/windows.js).
const registryApp = { get windows() { return WINDOWS }, sessionAnywhere: (id) => sessionAnywhere(id), sessionTitle: (id) => sessionTitle(id), get macs() { return macs } }
const opening = new Map()
/// A window's own stylesheet, by its path under the app root (core/assets.js), linked once; a window without one
/// simply has none.
const styled = new Set()
function loadStyles(path) {
  const href = appURL(path)
  if (styled.has(href)) return
  styled.add(href)
  fetch(href, { method: 'HEAD' }).then((r) => {
    if (!r.ok || !/css/.test(r.headers.get('content-type') || '')) return
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = href
    // the painter only sees DOM changes: once the styles are in, every HTML window paints again
    link.onload = () => world?.restyled()
    document.head.appendChild(link)
  }).catch(() => {})
}
/// A session by id on whichever Mac lists it.
function sessionAnywhere(id) { for (const m of macs.list) { const s = m.client?.sessions?.find((x) => x.id === id); if (s) return s } return null }
/// A session's title (core/session-title.js), its own Mac `key` asked first: the row an extension lists it under (a
/// Claude conversation by Claude's title — Josh 10-10: every window on the phone read "Session"), else opencode's
/// list, live or kept; null while no Mac lists it.
function sessionTitle(id, key = null) {
  const own = key ? macs.get(key) : null
  for (const m of own ? [own, ...macs.list.filter((x) => x !== own)] : macs.list) {
    let client = m.client
    if (!client?.sessions?.length) { try { client = offlineClient(m) || client } catch {} }
    const t = titleOf({ ext: Extensions.homeRows(m.key), client }, id)
    if (t) return t
  }
  return null
}
/// Which Mac a new window belongs to: a session's own Mac, the Mac Files on a Mac was asked for, the Mac of the
/// window that opened it, or the current one.
function macKeyFor(kind, arg, from) {
  if (kind === 'session') {
    const id = sessionOf(arg)
    if (macs.get(from)?.client?.sessions?.some((x) => x.id === id)) return from
    const owner = macs.list.find((m) => m.client?.sessions?.some((x) => x.id === id))
    if (owner) return owner.key
  }
  if (kind === 'macfiles' && typeof arg === 'string' && macs.get(arg)) return arg
  return macs.get(from)?.key || macs.current?.key || null
}
/// Open a window of a kind (or bring it forward when it is open). Resolves to its spec, or null. `from` is the
/// Mac of whatever opened it: the window belongs to that Mac.
async function openWindow(kind, arg = null, from = null, owner = null) {
  if (kind === 'home') { world.go(WINDOWS.find((w) => w.id === 'home')); return null }
  const k = windowSpec(kind)
  if (!k) { liveStatus.textContent = kind + ' is not in the web app yet.'; return null }
  const macKey = macKeyFor(kind, arg, from)
  const id = k.key ? k.key(arg, macKey, registryApp) : 'w-' + kind
  const have = WINDOWS.find((w) => w.id === id)
  // one of a kind for whichever Mac (New Window, Mac Windows): asked for another Mac, it moves to that one
  if (have) { if (macKey && have.macKey !== macKey && have.perMac) { retarget(have, macKey); have.dom?.again?.(arg); have.dom?.render?.() } world.go(have); return have }
  if (opening.has(id)) return opening.get(id)
  const made = (async () => {
    let mod
    try { mod = await k.module() } catch (e) { liveStatus.textContent = k.title(arg, registryApp) + ' is not in the web app yet.'; logLine('window ' + kind + ': ' + e.message); return null }
    for (const path of windowStyles(k)) loadStyles(path)
    const base = k.title(arg, registryApp), title = macTitled(k, base, macKey)
    const spec = { id, kind: 'html', winKind: kind, closable: true, short: clip(title, SHORT), name: title, angle: 0, lift: 0, radius: k.place?.[1] === 'front' ? RADIUS - 0.35 : RADIUS, rule: k.place }
    if (forOneMac(k)) { spec.baseName = base; spec.perMac = !k.key }
    if (kind === 'session') spec.sessionID = sessionOf(arg)
    // whose it is, for Snap's row (core/snap-layout.js): the session window that opened it, or the session it is about
    else { if (owner?.sessionID) spec.ownerID = owner.id; if (arg && typeof arg === 'object' && typeof arg.sessionID === 'string') spec.forSession = arg.sessionID }
    spec.macKey = macKey
    if (k.at) { spec.angle = k.at.angle; spec.lift = k.at.lift; spec.placed = true }
    let win
    try { win = new mod.default(windowContext(spec), arg) } catch (e) { fail(e); logLine('window ' + kind + ': ' + (e.stack || e.message)); return null }
    spec.dom = win
    sizeFromPoints(spec, win.size?.w || 620, win.size?.h || 760)
    WINDOWS.push(spec)
    if (spec.sessionID) sessionWindows.push(spec)
    LIVE_WINDOWS.add(id)
    world.add(spec)
    relayout(); applyLive()
    world.go(spec)
    return spec
  })()
  opening.set(id, made)
  try { return await made } finally { opening.delete(id) }
}
/// A window's size in points → metres on the arc (visionOS: 1360 points a metre at one metre, scaled with distance).
function sizeFromPoints(spec, w, h) {
  const r = spec.radius ?? RADIUS
  spec.logical = w; spec.px = w * 2
  spec.w = (w * r) / PPM; spec.h = (h * r) / PPM
}
/// A window for one Mac — one per Mac (its key names the Mac) or one that follows its Mac picker (`perMac`).
const forOneMac = (k) => !!k && (k.perMac ?? (typeof k.key === 'function' && k.key.length >= 2))
/// Its title names the Mac when there are several (widget-titles-name-the-mac.md): "Usage · DXNY348".
function macTitled(k, base, macKey) {
  const m = macs.get(macKey)
  return forOneMac(k) && m && macs.several ? `${base} · ${m.name}` : base
}
/// A window moved to another Mac (its Mac picker, or New Session asked for on another tab): its Mac, its title.
function retarget(spec, key) {
  const m = macs.get(key)
  if (!m || m.paused) return false
  spec.macKey = key
  if (spec.baseName) { spec.name = macTitled(windowSpec(spec.winKind), spec.baseName, key); spec.short = clip(spec.name, SHORT); world?.drawStrip?.() }
  spec.dom?.macChanged?.()
  return true
}
/// A window asks for a new size (the session window grows with its panes): the world follows, the row or arc with it.
function resizeHtmlWindow(spec, w, h) {
  const was = spec.w
  sizeFromPoints(spec, w, h)
  world?.resized(spec)
  if (Math.abs(was - spec.w) > 1e-4) relayout()
}
/// What a window can reach (the ctx of docs/wireframe/web-windows.md).
const eventFns = new Map()   // fn → the spec of the window that asked (its Mac's events only)
const SETTINGS = {
  get(key, fallback) { try { const v = localStorage.getItem('cody.setting.' + key); return v == null ? fallback : JSON.parse(v) } catch { return fallback } },
  set(key, value) { try { localStorage.setItem('cody.setting.' + key, JSON.stringify(value)) } catch {} repaint() },
}
/// opencode's events go to the windows of the Mac they came from.
function eventFrom(mac, ev) {
  for (const [fn, spec] of eventFns) { if (macOf(spec) !== mac) continue; try { fn(ev) } catch (err) { console.error(err) } }
}
function windowContext(spec) {
  return {
    // a session window whose Mac is out of reach (and whose live client never loaded it): what this device kept —
    // the same window, offline (offline/client.js); the live Client again once the link is up
    get client() {
      const c = clientOf(spec), m = macOf(spec)
      return m && spec.sessionID && m.link !== 'online' && !c?.views?.has(spec.sessionID) ? offlineClient(m) : c
    },
    get offline() { return isOfflineClient(this.client) },
    get offlineLine() { return this.offline ? offlineLineFor(macOf(spec)?.key, spec.sessionID) : '' },
    keptFor: (session) => keptFor(macOf(spec)?.key, session),
    // the turns this device kept for a session (offline/, read from IndexedDB at start), synchronously: a session
    // window starts from them before any fetch, and keeps them whatever the link does
    keptView: (session) => { const m = macOf(spec); return m && session ? offlineClient(m)?.view(session) || null : null },
    eraseKept: (id) => eraseKept(id),
    retryKept: (id) => retryKept(id),
    get mac() { return macOf(spec)?.name || 'Your Mac' },
    sessionTitle: (id) => sessionTitle(id, macOf(spec)?.key || null),
    get macID() { return macOf(spec)?.key || null },
    get link() { return macOf(spec)?.link || 'offline' },
    get linkWhy() { return macOf(spec)?.why || '' },
    request: (method, path, opts) => { const c = clientOf(spec); return c ? c.transport.request(method, path, opts) : Promise.reject(Object.assign(new Error('Not connected to the Mac.'), { status: 0 })) },
    // a prompt that can't reach the Mac now: kept on this device, sent when its link opens (offline/)
    // (none without a paired Mac — the demo: the old "Not connected" stands)
    get keep() { const m = macOf(spec); return m ? (fields) => keepPrompt({ mac: m.key, macName: m.name, ...fields }) : null },
    // an opencode prompt on its way: on this page's disk first, gone on its 2xx, kept if cut off (offline/ahead.js)
    get ahead() { const m = macOf(spec); return m ? (fields) => promptAhead({ mac: m.key, macName: m.name, ...fields }) : null },
    aheadDone: (id) => promptSent(id),
    aheadCutOff: (id) => promptCutOff(id),
    // a held card's way (Resume in Terminal, Send through Chrome, …), tapped under a kept prompt
    chooseKept: (id, choice) => chooseKept(id, choice),
    cancelInbox: (entry) => cancelInbox(macOf(spec)?.key, entry),
    onEvent: (fn) => { eventFns.set(fn, spec); return () => eventFns.delete(fn) },
    socket: (path, opts) => clientOf(spec)?.transport.socket?.(path, opts) || null,
    get files() { return clientOf(spec)?.transport.files?.() || null },
    // the Mac picker, where a Mac has to be chosen (New Session, Mac Windows): the window moves to that Mac, and
    // it becomes the default next time
    get macs() { return macs.list.map((m) => ({ id: m.key, name: m.name, link: m.link, paused: m.paused })) },
    // paused Macs are not offered; the window's own Mac stays listed (marked) so the picker names it truly
    get macHosts() {
      const own = macOf(spec)?.key
      const hosts = macs.list.filter((m) => !m.paused || m.key === own).map((m) => ({ id: m.key, name: m.paused ? m.name + ' (Paused)' : m.name }))
      return hosts.length > 1 ? hosts : undefined
    },
    get macHost() { return macOf(spec)?.key || null },
    // …and Home's tab with it: one current Mac for the whole app (home-mac-tabs.md › The tab is the Mac)
    chooseMac: (key) => { if (!retarget(spec, key)) return; macs.choose(key); homeWin.follow(key); spec.dom?.render?.() },
    open: (kind, arg) => openWindow(kind, arg, spec.macKey, spec),
    close: () => closeWindow(spec),
    resize: (w, h) => resizeHtmlWindow(spec, w, h),
    note: (text) => { liveStatus.textContent = text },
    settings: SETTINGS,
    tool: (name, on) => useTool(name, on),
    toolAvailable: (name) => (name === 'pasteFromMac' || name === 'sendToMac' ? !!macWindows : true),   // the current Mac's
    toolOn: (name) => world?.toolOn?.(name),
    toolStatus: () => toolNote,
    pair: (link) => { location.href = link },
    get macWindows() { return macOf(spec)?.windows || null },
    get paired() { return !!connection && !!macOf(spec) },
    get openSessions() { return sessionWindows.map((w) => w.sessionID) },
    openURL: (url) => window.open(url, '_blank', 'noopener'),
    openMacWindow: (w, how) => openMacWindow(w, macOf(spec), { ...how, owner: spec }),
    // a Mac window streamed inside this window's pane (core/embedded-streams.js): its spec (player, window), or null
    embedMacWindow: (w) => embedMacWindow(w, macOf(spec), spec),
    closeEmbedded: (embedded) => { if (embedded?.embed === spec) closeStream(embedded) },
    // a key an extension's key row sends into one of this Mac's streams (only what the person pressed): false when
    // that window isn't streaming here
    streamInput: (windowId, ev) => {
      const s = streams.find((x) => x.window.id === windowId && x.macKey === macOf(spec)?.key)
      if (!s) return false
      s.player?.touched?.(); s.stream.send('input', ev); return true
    },
    refreshMacWindows: () => { const mw = macOf(spec)?.windows; if (mw) { mw.list(); mw.requestApps() } },
    launchApp: (bundleID) => launchApp(bundleID, macOf(spec)),
    // Tiny Engineer's robots (robot-crew.js): the roster store, a window's live robot, Choose Robot's pick
    get robots() { return robotsStore },
    figure: (key) => world?.figure?.(spec, key) || null,   // a live robot: in the immersive room only
    pickRobot: (sessionID, robotID) => {
      const s = sessionWindows.find((w) => w.sessionID === sessionID && macOf(w) === macOf(spec))
      if (s && world?.swapRobot) world.swapRobot(s, robotID); else robotsStore.assign(macOf(spec)?.key, sessionID, robotID)
    },
    // Snap: the screen a window lays itself out at (a session window, Home), else null — the headset's size
    get screen() { return world?.screen?.(spec) || null },
  }
}
/// The Status Orb (windows/orb/orb.js): a volume of spheres, one per open session window — a three.js object, so it
/// stands in the immersive room (render-gl.js makes it); on a screen it waits there.
let orbCount = 0
function openOrb() {
  const W = 0.8, H = 0.62, id = 'orb-' + ++orbCount
  const spec = { id, kind: 'orb', closable: true, short: 'Status Orb', name: 'Status Orb', angle: 0, lift: -0.02, radius: RADIUS, rule: ['session', 'leading'], w: W, h: H }
  spec.logical = (W * PPM) / RADIUS; spec.w0 = W; spec.logical0 = spec.logical
  WINDOWS.push(spec)
  LIVE_WINDOWS.add(id)
  world.add(spec)
  relayout(); applyLive()
  if (world.kind !== 'gl') liveStatus.textContent = 'The Status Orb stands in the room: enter VR (or AR) to see it.'
}

/// Tiny Engineer beside each session window: the roster from the Mac (tiny-engineer/roster.js); the robots
/// themselves stand in the immersive room (robot-crew.js, render-gl.js), and on a screen the session in front shows
/// its robot as a badge that still says its phrases (tiny-engineer/robot-badges.js, render-dom.js).
const robotsStore = new RobotsStore({ macs })

/// What the Tools bubble switches and does (ToolsBubble.swift): the air keyboard and its Recenter, the hand
/// skeleton, and the shared clipboard with the Mac. `toolNote` is the bubble's status line.
let toolNote = ''
function useTool(name, on) {
  const setNote = (text) => { toolNote = text; repaint() }
  if (name === 'airKeyboard' || name === 'recenter' || name === 'handSkeleton') {
    // the air keyboard and the hands are the immersive room's (render-gl.js)
    const said = world?.tool?.(name, on)
    if (said === 'hidden') setNote('')
    else setNote(said || (on === false ? '' : name === 'handSkeleton' ? 'The hands show in VR, when the headset tracks them.' : 'The air keyboard is for VR; here the phone’s own keyboard types into the field you tap.'))
    return
  }
  if (name === 'pasteFromMac') {
    if (!macWindows) { setNote('Not connected to the Mac.'); return }
    macWindows.onClipboard = ({ text = '' }) => {
      navigator.clipboard?.writeText(text).then(() => setNote(`Pasted ${text.length} characters from the Mac.`), () => {
        // the browser wants a tap for the clipboard: the text goes into the field being typed in, if there is one
        if (liveField()) { fieldSync(kbField.value + text); setNote(`Pasted ${text.length} characters into the field.`) } else setNote("The browser would not take the Mac's clipboard; tap a text field first.")
      })
    }
    macWindows.clipboardGet(); setNote('Asking the Mac…')
    return
  }
  if (name === 'sendToMac') {
    if (!macWindows) { setNote('Not connected to the Mac.'); return }
    const put = (text) => { macWindows.clipboardPut(text); setNote(`Sent ${text.length} characters to the Mac.`) }
    if (liveField()?.value) { put(kbField.value); return }
    navigator.clipboard?.readText().then(put, () => setNote('The browser would not share its clipboard.')) ?? setNote('This browser has no clipboard to send.')
    return
  }
  liveStatus.textContent = name + ' is not in the web app yet.'
}
/// Home's state: one section per paired Mac — its name, its link and route, its sessions and who is working.
function homeState() {
  return {
    paired: !!connection,
    current: macs.current?.key || null,
    overCap: savedRooms().length > ROOM_CAP ? { cap: ROOM_CAP, newest: savedRooms()[0]?.room } : null,
    macs: macs.list.map((m) => ({ m, off: offlineHome(m, { sessions: m.client?.sessions || [], ext: Extensions.homeRows(m.key), extGroups: Extensions.homeGroups(m.key) }) })).map(({ m, off }) => ({
      key: m.key, name: m.name, link: m.link, why: m.why, route: m.route?.label || '', relayOnly: !!m.transport?.relayOnly,
      routeInfo: m.route || null, relayOnlySetting: relayOnly(), relayStatus: m.relayStatus || null, macState: m.macState || null,
      mcp: m.mcp || [], mcpBusy: m.mcpBusy || null, mcpErrors: m.mcpErrors || null,
      relay: m.relay, paused: m.paused,
      problems: m.client?.problems?.(m.name) || [],
      schedule: scheduleOf(m), scheduleAllowed: !!m.windows?.schedule?.allowed, scheduleAsked: !!m.scheduleAsked, scheduleRefused: m.scheduleRefused || '',
      tunnel: !m.room, sessions: off.sessions,
      // offline-first (offline/): the kept list while the link is down, "Offline · as of 14:05" under its name
      offline: off.offline,
      // the sessions with a window open here: first in Home's Recent order (windows/home/order.js)
      openHere: sessionWindows.filter((w) => (w.macKey || macs.current?.key) === m.key).map((w) => w.sessionID),
      update: m.windows?.update || null,
      status: (id) => { const st = m.client?.views.get(id)?.status || m.client?.statuses.get(id); return st === 'busy' || st === 'retry' ? 'working' : 'idle' },
      // Needs you: a permission or a question waiting (homeasks.js)
      waiting: (id) => !!asksOf(m.client, (method, path, opts) => m.client.transport.request(method, path, opts))?.waiting(id),
      // the Mac's extensions: their Home rows (extensions.js)
      // a conversation and its running Terminal continuation: one row, the original's (windows/home/continuations.js)
      ext: collapseContinuations(off.ext), extGroups: off.extGroups, extNotices: Extensions.notices(m.key),
      // its archived sessions' list (the Archived button — windows/home/archived.js)
      extArchived: Extensions.homeArchived(m.key),
      usage: m.windows?.usage || null, cards: m.windows?.cards || null, saved: m.windows?.saved || null,
      // runs the dev Mac app (it serves To Test): Home's More offers To Test
      testing: m.link === 'online' && m.windows?.tester?.testing === true,
    })),
  }
}
/// `client` and `macWindows` follow the current Mac; the badge and the sheet's routes follow every Mac.
function syncCurrent() {
  const cur = macs.current
  client = cur?.client || null
  macWindows = cur?.windows || null
  drawRoutes()
  if (!connection) return
  const up = macs.list.filter((m) => m.link === 'online').length
  if (macs.several) setBadge(`Live · ${up} of ${macs.active.length} Macs`)
  else if (cur) setBadge((cur.link === 'online' ? 'Live · direct to ' : 'Live · ') + cur.name)
}

/// Home's toolbar, its ⋯ menu and a Mac's ⋯ (core/home-items.js): the item's own `open`, else the window of that
/// kind; what the web app does not have yet says so.
const homeApp = { openWindow, openPicker: () => openPicker(), openOrb: () => openOrb(), get macs() { return macs } }
function openFromHome(id, key) {
  // a Mac picked from Home (its tab, its ⋯, the which-Mac menu): the current Mac, remembered, its tab open
  if (key && macs.get(key)) { macs.choose(key); homeWin.follow(key) }
  const item = homeItem(id)
  if (item?.open) { item.open(homeApp, key); return }
  if (hasWindow(id)) { openWindow(id, null, key || null); return }
  liveStatus.textContent = (item?.label || id) + ' is not in the web app yet.'
}
function openPicker() {
  const spec = WINDOWS.find((w) => w.id === 'macapps')
  ensureCanvas(spec)
  LIVE_WINDOWS.add('macapps'); world.add(spec); applyLive(); relayout()
  world.go(spec)
}
/// A new session in a folder, on the Mac of the session in front (or the current Mac).
async function newSessionIn(dir, mac = macOf(currentSession()) || macs.current) {
  const c = mac?.client
  if (!dir || !c) return
  try {
    const made = await c.transport.request('POST', 'session', { query: { directory: dir }, body: {} })
    await c.loadSessions()
    openSession(c.sessions.find((x) => x.id === made?.id), mac.key)
  } catch (e) { fail(e) }
}
/// Home's Delete N Sessions: `items` [{key, id}], each on its own Mac.
async function deleteSessions(items) {
  for (const { id } of items) { const w = sessionWindows.find((x) => x.sessionID === id); if (w) closeWindow(w) }
  try { for (const { key, id } of items) await macs.get(key)?.client?.deleteSession(id) } catch (e) { fail(e) }
  repaint()
}
/// Forget This Mac: that Mac's room and reconnect token go and its windows close; the other Macs stay. With
/// none left (or the tunnel door forgotten) the app starts over unpaired.
function forgetThisMac(key) {
  const mac = macs.get(key)
  if (!mac) return
  if (!mac.room) { forgetConnection(); location.href = location.pathname; return }
  for (const x of [...streams]) if (x.macKey === key) closeStream(x)
  for (const w of [...WINDOWS]) if (w.macKey === key && w.kind === 'html') closeWindow(w)
  macs.forget(key)
  if (!macs.list.length) { location.href = location.pathname; return }
  liveStatus.textContent = mac.name + ' is forgotten.'
  syncCurrent(); repaint()
}
/// visionOS's hover on HTML windows: the element under the pointer's ray lights; every other window's clears.
// the windows the app starts with, in the world that draws them (the DOM, until an immersive session)
let world = null, dom = null, gl = null
// `?go=files` lands on a window straight away — for links, and for the capture script that photographs the
// same window here and in the simulator to show both renderers reading one token.
const wantedAtStart = new URLSearchParams(location.search).get('go')

/// What is in the arc: with a Mac paired, the windows that exist — the session window, Home with the Mac's
/// sessions, a Mac window once one is streamed. Nothing paired, nothing in the arc: the bar and the sheet say
/// how to pair. There is no demo content (Josh, 09-27: "get rid of all demo mode content and we build it later
/// when we have everything working, and we know what data to mock").
const LIVE_WINDOWS = new Set(['home'])
function applyLive() {
  const paired = !!connection
  document.body.dataset.live = String(paired)
  if (!paired) { const sheet = document.getElementById('sheet'); if (sheet) sheet.hidden = false }
  if (!world) return
  world.sync()
  world.drawStrip()
  if (paired && (!world.current || !LIVE_WINDOWS.has(world.current.id))) world.go(sessionWindows[0] || WINDOWS.find((w) => w.id === 'home'))
  repaintLiveWindows()
}

/// The other windows that are live — Home with the Mac's sessions — are painted again with the live state
/// whenever it changes (they were painted once at startup, with the pictures, and never again). The Mac window
/// is left alone: its picture is the stream.
function repaintLiveWindows() {
  const live = liveState()
  // a session window opened before its Mac listed it (a restored layout, a link), or renamed since: its tab and strip
  if (retitle(WINDOWS, (spec) => sessionTitle(spec.sessionID, spec.macKey)).length) world?.drawStrip?.()
  for (const spec of WINDOWS) {
    if (!LIVE_WINDOWS.has(spec.id) && spec.id !== 'home') continue
    if (spec.dom) { try { spec.dom.render(spec.id === 'home' ? homeState() : undefined) } catch (e) { console.error(e) } continue }
    if (spec.stream || !spec.context) continue   // a stream's picture is its frames, never a painter
    spec.context.setTransform(1, 0, 0, 1, 0, 0)
    spec.context.clearRect(0, 0, spec.canvas.width, spec.canvas.height)
    spec.context.scale(spec.scale, spec.scale)
    spec.draw(spec.context, spec.logical, Math.round(spec.canvas.height / spec.scale), spec.kind === 'session' ? sessionLive(spec) : live)
    if (spec.id === 'macapps' && live) spec.tiles = live.macTiles
    world?.canvasChanged(spec)
  }
}




// MARK: WebXR
//
// On a headset this stops being a picture of the app and becomes the shape of it, at the right size, with the
// windows where they would actually be. Which is the whole argument for the port.


// Entering VR or AR (core/xr-button.js): the session asked for inside the tap, then the room loaded for it
// (core/render-gl.js, three.js with it) and the windows handed over; when it ends they come back to the DOM.
installXRButton({
  logLine,
  say: (text) => { liveStatus.textContent = text; live.hidden = false },
  onSession: (session, opts) => enterXR(session, opts),
  onEnd: () => leaveXR(),
})
async function enterXR(session, opts) {
  const { startGL } = await import('./core/render-gl.js')
  dom.suspend()
  try { gl = await startGL(glApp, session, opts) } catch (e) { dom.resume(); throw e }
  world = gl
  relayout()
  applyLive()
  applyStreamAttention()
}
function leaveXR() {
  if (!gl) return
  if (kbField) endFieldTyping()
  try { gl.dispose() } catch (e) { console.error(e) }
  gl = null
  world = dom
  dom.resume()
  relayout()
  applyLive()
  applyStreamAttention()
}
/// Whether an immersive session is drawing (the page's own prompt row then stands in for a window's field).
const presenting = () => world?.kind === 'gl'

// MARK: live — a real Mac behind the session window
//
// With a connection (this page served from a Mac's /xr/, or the site's /demo/ handed a Mac and a token by
// /connect/), the session window stops being a picture: it shows the newest session on that Mac, follows the
// event stream, and the box below the scene sends prompts. Everything else in the room is still the mock.

let client = null
const live = document.getElementById('live')
const liveStatus = document.getElementById('live-status')
const liveSessions = document.getElementById('live-sessions')
const promptBox = document.getElementById('prompt')
takeRelayGift()   // a relay gift in the address (relaygift/): out of the address bar before anything reads it, kept in memory
const connection = even ? evenConnection(even) : savedConnection()


/// Demo mode: the panels draw their built-in pictures although a Mac is paired — to see the whole app before
/// pairing, or to show it around. Kept across navigation; the connection stays up underneath.

/// What the painters get: null draws the mock; otherwise the open session and everything else the Mac told us.
function liveState() {
  // a Mac is paired but not reached yet: the window says so instead of showing the demo pictures
  if (!client) return connection ? { mac: macs.current?.name || 'the Mac', title: macs.current?.name || 'Your Mac', subtitle: 'connecting…', transcript: [], connecting: true, prompt: '', status: 'idle', sessions: [], todos: [], diff: [], activity: [], providers: [], defaultModel: null, ownPrompt: false } : null
  const s = client.session
  return {
    mac: macs.current?.name || 'the Mac',
    title: s ? s.title : 'No session open',
    subtitle: s ? (s.directory.replace(/^\/Users\/[^/]+/, '~') || 'no folder') : 'pick one under the scene',
    transcript: client.transcript(),
    prompt: promptBox?.value || '',
    ownPrompt: presenting(),   // the page's prompt bar is there unless we are inside a headset
    status: client.status,
    sessions: client.sessions,
    todos: client.todos,
    diff: client.diff,
    activity: client.activity(),
    providers: client.providers,
    defaultModel: client.defaultModel,
    macWindows: macWindows?.windows || [],
    streaming: streams.map((x) => x.window.id),
    appIcons,
    openIDs: sessionWindows.map((x) => x.sessionID),
    pendingDelete,
    macTiles: [],   // the picker's painter fills this with where each tile is, for a tap to land on one
  }
}

let repaintQueued = false
let pendingDelete = null   // a session whose bin was tapped once: the next tap deletes it
function repaint() {
  if (repaintQueued) return
  repaintQueued = true
  requestAnimationFrame(() => { repaintQueued = false; repaintLiveWindows() })
}

function listSessions() { repaint() }   // the sessions are Home's rows and their own windows now, not chips

function fail(e) {
  liveStatus.textContent = e.message || String(e)
  if (e.status === 401 && !connection?.rooms) forgetConnection()
}

// the Live badge, the sheet's Connection part, pairing and installing (core/live.js)
const { setBadge, drawRoutes, drawState, pairThisDevice, installControls } = makeLive({ macs, connection, liveStatus, live, versionText, relayAction, pair: even ? () => even.openPairing() : null })

/// The direct path: ask the Mac where its room is, join it, pass the passkey challenge, and if a WebRTC
/// connection comes up, move the client onto it. The tunnel stays as the fallback either way.
async function goDirect(tunnel) {
  if (!connection.sameOrigin && !connection.token) return
  let where
  try { where = await tunnel.request('GET', 'auth/room') } catch (e) { liveStatus.textContent = 'No room: ' + e.message; return }
  if (!where?.room) return
  try {
    // the relay is the Mac's choice, and connectDirect asks for it only when it is needed: a direct-first Mac
    // brings it in when direct fails (core/directfirst.js); an older one has it fetched there from its challenge
    const iceServers = where.relay === 'ours' ? undefined : where.iceServers || undefined
    const direct = await connectDirect({ site: where.site, room: where.room, iceServers, onStatus: (s) => { liveStatus.textContent = s } })
    client.transport = direct
    const mac = macs.get('tunnel')
    if (mac) watchRoute(direct, (route) => { mac.route = route; repaint(); drawRoutes() })
    setBadge('Live · direct to ' + (where.mac || 'the Mac'))
    liveStatus.textContent = 'Direct WebRTC connection to the Mac.'
    if (direct.wire && mac) watchWindows(mac, direct.wire)
    return direct
  } catch (e) { liveStatus.textContent = 'Direct failed (' + e.message + ') — staying on the tunnel.'; return null }
}


// the meter: what the connection costs, the stream's frame rate (core/meter.js)
startMeter({ pc: () => client?.transport?.pc, streams: () => streams, connected: () => !!client })

/// Sessions: one window in the arc per open session, several side by side, as on the headset (Josh, 09-27: "we
/// shouldn't be using chips or buttons to switch sessions … they can all live next to eachother"). Each keeps
/// its own transcript in the client's views, all live from the one event stream; Home opens them.
const sessionWindows = []
let htmlSessions = null   // null until the first try: true when windows/session.js is there, false to paint them
const currentSession = () => (world?.current?.kind === 'session' ? world.current : null)
function sessionLive(spec) {
  const c = clientOf(spec)
  const v = c?.views.get(spec.sessionID)
  const s = v?.session || spec.session
  const front = currentSession() === spec
  return {
    mac: macOf(spec)?.name || 'the Mac',
    title: s.title, subtitle: s.directory.replace(/^\/Users\/[^/]+/, '~') || 'no folder',
    transcript: c ? c.transcriptOf(spec.sessionID) : [], connecting: !v,
    prompt: (presenting() ? world.kbTarget === spec : front) ? (promptBox?.value || '') : '',
    ownPrompt: presenting() && world.kbTarget === spec,
    status: v?.status || 'idle', sessions: c?.sessions || [], todos: v?.todos || [],
  }
}
/// A session's window, on the Mac `key` (or the Mac that lists it).
function openSession(s, key = null) {
  if (!s) return
  key = macKeyFor('session', s.id, key)
  const c = macs.get(key)?.client
  const have = sessionWindows.find((x) => x.sessionID === s.id)
  if (have) { world.go(have); return }
  // its Mac out of reach: the same window over what this device kept (offline/ — windowContext's client), live again
  // with the link
  if (!c && !macs.get(key)) return
  if (htmlSessions !== false) {
    openWindow('session', s.id, key).then((spec) => { if (!spec && htmlSessions === null) { htmlSessions = false; openSession(s, key) } else if (spec) htmlSessions = true })
    return
  }
  if (!c) return
  const spec = {
    id: 'session-' + s.id, kind: 'session', sessionID: s.id, session: s, closable: true, macKey: key,
    short: clip(s.title, SHORT), name: s.title, logical: 1300, draw: Panels.transcriptPane, ...place('session'), px: 2000,
  }
  WINDOWS.push(spec); sessionWindows.push(spec)
  ensureCanvas(spec)
  LIVE_WINDOWS.add(spec.id)
  world.add(spec)
  relayout(); applyLive()
  world.go(spec)
  c.openView(s.id).then(repaint).catch(fail)
}
function closeSession(spec) {
  const i = sessionWindows.indexOf(spec); if (i < 0) return
  sessionWindows.splice(i, 1)
  clientOf(spec)?.closeView(spec.sessionID)
  if (world.kbTarget === spec) world.endTyping?.()
  removeWindow(spec)
}
/// A window leaves the arc: out of the world, out of the lists; the view moves on.
function removeWindow(spec) {
  world.remove(spec)
  if (WINDOWS.includes(spec)) WINDOWS.splice(WINDOWS.indexOf(spec), 1)
  LIVE_WINDOWS.delete(spec.id)
  if (world.current === spec) world.current = null
  relayout()
  if (!world.current) world.go(sessionWindows[0] || WINDOWS.find((w) => w.id === 'home'))
  drawControlPill(); drawInputBar()
  repaint()
}

/// The prompt bar is for a window that takes typing — a session in front — and hides otherwise (Josh, 09-27: "the
/// input box shows up when it's not focused on a window that needs it").
function drawInputBar() {
  const row = document.querySelector('#live .row')
  if (row) row.hidden = !currentSession() && !kbField
  for (const id of ['stop', 'fresh']) { const b = document.getElementById(id); if (b) b.hidden = !!kbField }
}

/// Typing into a window's own field (a session's prompt, a sheet's search, a terminal). The field is a real element
/// in the off-screen DOM, and a keyboard only comes for something on screen, so the page's prompt row stands in:
/// it takes the phone's keyboard (the headset's system keyboard in VR), mirrors every change into the field and
/// passes Return, Tab, Escape, arrows and control chords on to it. The air keyboard types into the field directly.
let kbField = null, kbFieldAt = null   // the field, and where to find it again ({root, key}) if its window redraws
/// The field, found again when its window drew a new copy of it (windows redraw their DOM; data-key names it).
function liveField() {
  if (!kbField || kbField.isConnected || !kbFieldAt?.key) return kbField
  const again = kbFieldAt.root.querySelector(`[data-key="${CSS.escape(kbFieldAt.key)}"]`)
  if (again) kbField = again
  return kbField
}
document.getElementById('html-windows').addEventListener('focusin', (e) => {
  const el = e.target
  if (el !== kbField && el.matches?.('input, textarea')) startFieldTyping(el)
})
function startFieldTyping(el) {
  kbField = el
  const holder = document.getElementById('html-windows')
  let root = el; while (root.parentElement && root.parentElement !== holder) root = root.parentElement
  kbFieldAt = { root, key: el.dataset?.key || null }
  promptBox.value = el.value
  promptBox.placeholder = el.placeholder || ''
  document.getElementById('live').hidden = false
  drawInputBar()
  try { promptBox.focus({ preventScroll: true }) } catch {}
  if (presenting() && document.activeElement !== promptBox) world.showKeyboard?.(el.value)
  logLine('typing into a window field: ' + (document.activeElement === promptBox ? 'the keyboard has the stand-in' : 'no focus; air keyboard'))
}
function endFieldTyping() {
  const el = kbField
  kbField = null
  promptBox.placeholder = 'Message the agent'
  world?.hideKeyboard?.()
  try { el?.blur(); promptBox.blur() } catch {}
  drawInputBar()
}
/// The field's value changed here: tell it (its window reads it on `input`), then take back what it kept.
function fieldSync(value) {
  if (!liveField()) return
  kbField.value = value
  kbField.dispatchEvent(new Event('input', { bubbles: true }))
  if (liveField() && promptBox.value !== kbField.value) promptBox.value = kbField.value
  world?.keyboardText?.(kbField.value)
}
/// A key for the field itself (Return sends, Tab completes, a terminal wants them all): true when it took it.
function fieldPress(init) {
  if (!liveField()) return false
  const ev = new KeyboardEvent('keydown', { bubbles: true, cancelable: true, ...init })
  kbField.dispatchEvent(ev)
  if (liveField()) { promptBox.value = kbField.value; world?.keyboardText?.(kbField.value) }
  return ev.defaultPrevented
}
function fieldKey(key) {
  if (!liveField()) return
  if (key.id === 'enter') { fieldPress({ key: 'Enter' }); return }
  if (key.id === 'backspace' && fieldPress({ key: 'Backspace' })) return   // the field took it (a terminal)
  fieldSync(airKeyEdit(kbField.value, key).text)
}
promptBox?.addEventListener('input', () => { if (kbField) fieldSync(promptBox.value) })
promptBox?.addEventListener('keydown', (e) => {
  if (!kbField) return
  const special = e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape' || e.key.startsWith('Arrow') || e.ctrlKey || e.metaKey || e.altKey
  if (!special || (e.key === 'Enter' && e.shiftKey)) return
  e.stopImmediatePropagation()
  const took = fieldPress({ key: e.key, code: e.code, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey })
  if (took || e.key === 'Enter' || e.key === 'Tab') e.preventDefault()
  if (e.key === 'Escape' && !took) endFieldTyping()
}, true)
promptBox?.addEventListener('blur', () => setTimeout(() => { if (kbField && document.activeElement !== promptBox && !presenting()) endFieldTyping() }, 250))
document.getElementById('send')?.addEventListener('click', (e) => { if (!kbField) return; e.stopImmediatePropagation(); fieldPress({ key: 'Enter' }) }, true)

/// Streams: one window in the arc per Mac window being watched, each over its own data channel to the Mac
/// (Josh, 09-27: "We also can't open multiple windows with multiple streams"). The main wire channel keeps the
/// list and the clipboard; a stream opens "wire:n" beside it.
const streams = []
let streamSeq = 0
const currentStream = () => (world?.current?.stream ? world.current : null)
/// Typing into the stream in front (streamkeys.js): each key goes to the Mac as it is pressed, no box in between.
const streamKeys = new StreamKeys({
  // the stream in front: a stream window, or the stream inside the front session window's pane that was tapped
  canvas: document.getElementById('stage'), log: logLine,
  current: () => currentStream() || streams.find((x) => x.embed && x.embed === world?.current && x === streamKeys.target) || null,
  send: (spec, ev) => { spec.player?.touched(); spec.stream.send('input', ev) },
})

/// A window from the picker: already streaming, it comes into view; otherwise a new window streams it — its picture
/// (a canvas the frames are drawn onto, at the Mac window's own proportions) inside the headset's chrome
/// (windows/macwindow.js: the ornament above, the note, the Pointer menu). The world shows them (render-dom.js: the
/// canvas in the chrome's DOM; render-gl.js: two planes). `stay`: open it without turning to it.
function openMacWindow(w, mac = macs.current, { stay = false, owner = null } = {}) {
  const have = streams.find((x) => x.window.id === w.id)
  if (have?.embed) { liveStatus.textContent = 'That window is streaming in its session’s pane.'; return }
  if (have) { if (!stay) world.go(have); return }
  const spec = streamSpec(w, mac)
  if (!spec) return
  if (owner?.sessionID) spec.ownerID = owner.id   // beside its session in Snap's row
  WINDOWS.push(spec)
  paintPicture(spec, null)
  spec.player = makePlayer(spec, spec.stream, w, { paintPicture, drawPointer: (x) => world.drawPointer?.(x), closeStream, openMacWindow, streamKeys, note: (text) => { liveStatus.textContent = text } })
  makeStreamChrome(spec)
  LIVE_WINDOWS.add(spec.id)
  world.add(spec)
  relayout()
  applyLive()
  if (!stay) world.go(spec)
  repaint()
}

/// A stream's spec and its own data channel to the Mac ("wire:n"); null when the Mac can't stream now.
function streamSpec(w, mac) {
  const pc = mac?.transport?.pc
  if (!mac?.windows || !pc) return null
  if (streams.length >= MAX_STREAMS) { liveStatus.textContent = 'Six windows are streaming already — close one first.'; return null }
  const n = ++streamSeq
  const channel = pc.createDataChannel('wire:' + n, { ordered: true })
  const link = new MacWindows(channel)
  const spec = {
    id: 'stream-' + n, short: (w.title || w.app).slice(0, 22), logical: 1000, draw: () => {}, ...place('mac'), px: 1600,
    name: w.app + (w.title ? ' — ' + w.title : ''), stream: link, channel, window: w, ptr: { x: 0.5, y: 0.5 }, snap: true, closable: true,
    macKey: mac.key,
  }
  spec.ptM = (spec.radius ?? RADIUS) / PPM            // metres per point at this window's distance
  const ratio = (w.width || 1000) / Math.max(1, w.height || 700)
  spec.w = 1000 * spec.ptM; spec.h = spec.w / ratio
  spec.canvas = document.createElement('canvas')
  spec.canvas.width = 1600; spec.canvas.height = Math.round(1600 / ratio)
  spec.picture = { x: 0, y: 0, w: spec.canvas.width, h: spec.canvas.height }
  streams.push(spec)
  return spec
}

/// A Mac window streamed inside `host`'s pane (the Agent pane's `beside` stream — the headset's ExtensionStreamPane):
/// the same stream, no window of its own in the arc — the world lays its picture in the pane's slot (render-dom.js:
/// the canvas in the slot; render-gl.js: a plane over it, core/embedded-streams.js). Already streaming there: that
/// one. Streaming as a window of its own: that window closes, the pane takes it.
function embedMacWindow(w, mac, host) {
  const have = streams.find((x) => x.window.id === w.id)
  if (have?.embed === host) return have
  if (have) closeStream(have)
  const spec = streamSpec(w, mac)
  if (!spec) return null
  spec.embed = host
  paintPicture(spec, null)
  world.add(spec)
  spec.player = makePlayer(spec, spec.stream, w, { paintPicture, drawPointer: (x) => world.drawPointer?.(x), closeStream, openMacWindow, streamKeys, note: (text) => { liveStatus.textContent = text } })
  // what the pane shows around the picture (the note, Bring Forward) follows the player: the host redraws
  spec.onPlayerChange = () => { try { host.dom?.render?.() } catch (e) { console.error(e) } }
  spec.player.start({ maxFPS: 30, pointerStyle: 'headset', idleDrop: true })
  return spec
}

/// The picture: a frame (or black while there is none) filling the canvas, the corners rounded as the window's.
function paintPicture(spec, frame) {
  const c = spec.canvas.getContext('2d'), W = spec.canvas.width, H = spec.canvas.height
  const r = spec.embed ? Math.round(W * 0.012) : 46 * (W / (spec.w / spec.ptM))   // a pane's picture: 14 pt corners, as the headset's
  c.setTransform(1, 0, 0, 1, 0, 0)
  c.clearRect(0, 0, W, H)
  c.save(); c.beginPath(); c.roundRect(0, 0, W, H, r); c.clip()
  if (frame) c.drawImage(frame, 0, 0, W, H)
  else { c.fillStyle = '#0b0d14'; c.fillRect(0, 0, W, H) }
  c.restore()
  spec.picture = { x: 0, y: 0, w: W, h: H }
  world?.drawPointer?.(spec)
  if (spec.locked) drawLockBanner(spec)   // a frame still in flight when the Mac locked keeps the band
  if (spec.texture) spec.texture.needsUpdate = true
}



/// The stream's chrome (windows/macwindow.js): its own DOM, reading the player; the world shows it (render-dom.js
/// with the picture inside it, render-gl.js painted on a plane in front of the picture's).
function makeStreamChrome(spec) {
  const chromeSpec = { id: spec.id + '-chrome', chrome: true, stream: spec }
  loadStyles('windows/macwindow/macwindow.css')
  const ctx = windowContext(spec)
  Object.defineProperty(ctx, 'stream', { get: () => spec.player })
  ctx.resize = () => world.resized(spec)
  spec.chromeSpec = chromeSpec
  const dom = new MacWindowChrome(ctx)
  chromeSpec.dom = dom
  document.getElementById('html-windows').appendChild(dom.root)
  dom.render()
}

/// Only the streams being looked at run at the chosen rate; the others trickle at one frame a second and catch
/// up with a keyframe when they come into view (core/stream-attention.js; the world says what is in view).
function applyStreamAttention() {
  if (!streams.length || !world) return
  applyAttention(streams, (spec) => world.inView(spec), getWantedFPS)
}
setInterval(applyStreamAttention, 1500)

/// A stream is done: the channel closes (the Mac stops encoding), the window leaves the arc.
function closeStream(spec) {
  const i = streams.indexOf(spec); if (i < 0) return
  spec.closed = true   // a pane holding it (agent/embed-keeper.js) opens it again only once it is closed
  streamKeys.forget(spec)
  streams.splice(i, 1)
  clearInterval(spec.idleTimer)
  try { spec.player?.recognizer?.stop() } catch {}
  world.remove(spec)
  if (spec.chromeSpec) { try { spec.chromeSpec.dom.dispose() } catch {} spec.chromeSpec.dom.root.remove() }
  try { spec.stream.decoder?.close() } catch {}
  try { spec.channel.close() } catch {}
  if (WINDOWS.includes(spec)) WINDOWS.splice(WINDOWS.indexOf(spec), 1)
  LIVE_WINDOWS.delete(spec.id)
  if (world.current === spec) world.current = null
  relayout()
  if (!world.current) world.go(sessionWindows[0] || WINDOWS.find((w) => w.id === 'home'))
  drawControlPill(); drawInputBar()
  repaint()
}
function closeAllStreams() { for (const s of [...streams]) closeStream(s) }
document.getElementById('close-stream').addEventListener('click', (e) => { e.preventDefault(); const s = currentStream(); if (s) closeStream(s) })

let macWindows = null   // the current Mac's (syncCurrent); each Mac keeps its own as mac.windows
function watchWindows(mac, channel) {
  const macWindows = mac.windows = new MacWindows(channel)
  // the Mac's own color, said in its robots roster: kept per Mac, Home and the laptops repaint (maccolors.js)
  macWindows.onMacColor = (name) => { if (rememberMacColor(mac.key, name)) repaint() }
  syncCurrent()
  let askedApps = false
  macWindows.onWindows = (list) => {
    window.cody && (window.cody.windows = list)
    repaint()
    if (!list.length) liveStatus.textContent = 'The Mac lists no windows (is window sharing on there?).'
    // the icons come after the list, never ahead of it: the apps reply is a megabyte and a half (117 apps with
    // their icons) and on the ordered channel it kept the list waiting nine seconds
    if (!askedApps) { askedApps = true; macWindows.requestApps() }
  }
  macWindows.onFailure = (why) => {
    liveStatus.textContent = 'Mac windows: ' + why
    if (launching) { launching.reject(new Error(why)); launching = null }
    repaint()
  }
  macWindows.onLaunched = (w) => { if (launching) { launching.resolve(w); launching = null } repaint() }
  // the apps' icons, for the picker's tiles: decoded once, the picker repainted as each one arrives
  macWindows.onApps = (list) => {
    for (const app of list) {
      if (!app.icon || appIcons[app.bundleID]) continue
      const img = new Image()
      img.onload = () => repaint()
      img.src = 'data:image/png;base64,' + app.icon
      appIcons[app.bundleID] = img
    }
  }
  macWindows.list()
  // workspaces on a schedule (schedulecard.js): where this Mac's stand, told again on this channel as they change
  macWindows.onSchedule = (msg) => {
    mac.scheduleRefused = msg?.detail || ''
    if (msg?.detail || !['stopped', 'failed'].includes(scheduleOf(mac)?.state)) mac.scheduleAsked = false
    repaint()
  }
  macWindows.scheduleGet()
  // where this Mac's app stands against its feed: the badge on Home (the Update Mac App panel asks for itself)
  macWindows.onUpdate = () => repaint()
  macWindows.updateRequest('status')
  // To Test (windows/totest.js): only a Mac running the dev Mac app says testing: true — then Home's More offers it
  macWindows.onTester = () => repaint()
  macWindows.testerGet()
  // the Mac's extensions (extensions.js): its list and every doc now, then as they change
  Extensions.attach(mac.key, macWindows)
  // the Mac's license (license/index.js): asked once per link, checked offline, kept on mac.license and logged
  watchLicense(mac, macWindows)
  // Dev Mode (devcode/index.js): asks whether this Mac serves the web app — staging and local hosts, a pinned Mac
  watchDevCode(mac, macWindows)
  // a relay gift opened here (relaygift/): this Mac is one to send it to
  offerRelayGift(mac)
  // how this connection goes and what it has used of the relay, as the Mac measures it (core/relaystatus.js)
  watchRelayStatus(mac, macWindows, repaint)
  // the Mac's state line under its name on Home — headless, lid, lock, battery, where a prompt goes (core/macstate.js)
  watchMacState(mac, macWindows, repaint)
  // its MCP servers that need a sign-in or can't connect: a card on Home, pressed on the Mac (core/mcp.js)
  watchMcp(mac, macWindows, repaint)
  // its canned prompts: every prompt bar's list, kept here for when the Mac is out of reach (canned/)
  watchCanned(mac, macWindows, repaint)
  // Send Logs (sendlogs/): a bundle kept for this Mac goes now; the ⋯ sheet offers Send Logs to it
  watchSendLogs(mac, macWindows)
  // offline-first (offline/): prompts kept while this Mac was out of reach go now, and its list is kept for next time
  offlineLinkUp(mac)
}
Extensions.on(() => repaint())
// the Mac asks this device to close a session's windows (Cody's close_session_window, approved — docs/features/cody-mcp.md): as × does
Extensions.onCloseSession((key, id) => { for (const w of WINDOWS.filter((x) => x.kind === 'session' && x.sessionID === id && (!x.macKey || x.macKey === key))) closeWindow(w) })
const appIcons = {}
/// Launch an app on the Mac: resolves with its first window when the Mac says it is up, or fails with its words.
let launching = null
function launchApp(bundleID, mac = macs.current) {
  const macWindows = mac?.windows
  if (!macWindows) return Promise.reject(new Error('No Mac is sharing windows.'))
  launching?.reject(new Error('Another launch took over.'))
  return new Promise((resolve, reject) => {
    launching = { resolve, reject }
    macWindows.launch(bundleID)
    setTimeout(() => { if (launching?.resolve === resolve) { launching = null; reject(new Error('The Mac did not open it in time.')) } }, 30000)
  })
}

/// The WebRTC front, and nothing else: this browser paired with each Mac over its room, so it connects to every
/// one of them — passkey challenge, offer, answer — with no address of any kind (macs.js keeps the links). The
/// rooms are remembered, so next time is one Face ID away, or none while a Mac's reconnect token lasts.
function goLiveDirect() {
  live.hidden = false
  macs.loadRooms()
  // the Mac tab open last on this device is the current Mac again (home-mac-tabs.md › The tab is the Mac); a Mac gone or paused: the last used
  if (macs.get(homeWin.tab) && !macs.get(homeWin.tab).paused) macs.currentKey = homeWin.tab
  syncCurrent()
  applyLive()
  macs.start()
}
/// A Mac's link came up: its windows and its sessions. No session window opens by itself (Josh 10-01: "it keeps
/// opening a random session window") — Home shows them, as the headset does.
async function macConnected(mac) {
  const direct = mac.transport
  syncCurrent()
  applyLive()
  checkForNewBuild('connected')
  if (direct.wire) watchWindows(mac, direct.wire)
  even?.macChanged(mac.key)   // the glasses add-on: this Mac's wire is up (its glance rides it)
  // Web Push (docs/flows/notifications.yaml): a Mac that (re)connects — just paired, or updated to a build with Web
  // Push — is asked at once whether it has this device's subscription
  try { dispatchEvent(new CustomEvent('cody:mac-connected', { detail: { key: mac.key } })) } catch {}
  await mac.client.loadSessions()
  repaint()
  liveStatus.textContent = (mac.client.sessions.length ? 'Direct WebRTC connection to ' : 'Connected — no sessions yet on ') + mac.name + '.'
}

async function goLive() {
  if (connection.rooms) return goLiveDirect()
  // the tunnel door (or this page served by the Mac): one Mac, kept as the only entry of the list
  const tunnel = new FetchTransport(connection)
  const mac = macs.add(new Mac('tunnel', new URL(connection.origin).hostname))
  client = mac.client = new Client(tunnel)
  mac.link = 'online'
  client.onChange = (what) => { if (what === 'sessions') listSessions(); repaint() }
  client.onEvent((ev) => eventFrom(mac, ev))
  applyLive()
  live.hidden = false
  setBadge('Live · ' + new URL(connection.origin).hostname)
  liveStatus.textContent = 'Asking the Mac for its sessions…'
  try {
    await client.loadSessions()
    listSessions(); repaint()
    liveStatus.textContent = client.sessions.length ? 'Connected.' : 'Connected — no sessions on this Mac yet.'
  } catch (e) { fail(e); return }
  if (!new URLSearchParams(location.search).has('tunnel')) await goDirect(tunnel)   // ?tunnel skips the direct attempt
  // the event stream, forever: back off when it drops, poll the open session when a proxy never lets it start
  let delay = 1000
  for (;;) {
    let spoke = false
    try {
      const timer = setTimeout(() => { if (!spoke) for (const w of sessionWindows) client.openView(w.sessionID).then(repaint).catch(() => {}) }, 8000)
      await client.stream()
      clearTimeout(timer)
      spoke = true
      delay = 1000
    } catch (e) { if (e.status === 401) { fail(e); return } }
    liveStatus.textContent = 'Stream dropped — reconnecting…'
    await new Promise((r) => setTimeout(r, delay))
    delay = Math.min(delay * 2, 30000)
    for (const w of sessionWindows) { try { await client.openView(w.sessionID) } catch {} }
    repaint()
  }
}

if (promptBox) {
  promptBox.addEventListener('input', repaint)
  promptBox.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendPrompt() } })
  document.getElementById('send').addEventListener('click', sendPrompt)
  document.getElementById('stop').addEventListener('click', () => { const w = currentSession(); if (w) clientOf(w)?.abortView(w.sessionID).catch(fail) })
  // a new session in the same folder as the one in front, opened as a window of its own
  document.getElementById('fresh').addEventListener('click', () => { const w = currentSession(); newSessionIn(w ? (clientOf(w)?.views.get(w.sessionID)?.session || w.session)?.directory : client?.sessions[0]?.directory) })
}
async function sendPrompt() {
  const text = promptBox.value.trim()
  const w = currentSession()
  if (!w || !text) return
  promptBox.value = ''
  repaint()
  // the Mac out of reach: kept on this device and sent when its link opens (offline/, "Never lose a prompt")
  const mac = macOf(w), session = clientOf(w)?.views.get(w.sessionID)?.session || w.session || {}
  const keep = (why = '') => keepPrompt({ mac: mac?.key, macName: mac?.name, session: w.sessionID, title: session.title, directory: session.directory, text, why }).catch(fail)
  if (mac && mac.link !== 'online') { keep(); return }
  // a Claude conversation (`watch-<uuid>`): the Claude way — the outbox over the glance wire, never prompt_async
  if (isClaudeSession(w.sessionID)) { if (mac) keep(); else fail(new Error('Not sent — the Mac isn’t connected.')); return }
  // on this page's disk before it goes (offline/ahead.js): a reload mid-send keeps it; gone on its 2xx
  const view = clientOf(w)?.views.get(w.sessionID)
  const ahead = mac ? promptAhead({ mac: mac.key, macName: mac.name, session: w.sessionID, title: session.title, directory: session.directory, text, before: userTurnsWith(view, text) }) : null
  // a send that failed: the words kept, "Not sent" with Retry (offline/route.js) — never a raw "answered 500"; cut off
  // on its way (status 0) it may have arrived: kept, looked for in the transcript at the next link
  try { await clientOf(w).promptTo(w.sessionID, text); promptSent(ahead?.id) } catch (e) {
    if (ahead && e?.status === 0) { promptCutOff(ahead.id); return }
    promptSent(ahead?.id)
    if (mac) keep(e?.status === 0 ? '' : (e?.message || 'Not sent')); else fail(e)
  }
}

// MARK: the modes, connecting, installing

for (const a of document.querySelectorAll('[data-mode]')) {
  const mode = a.dataset.mode
  if (mode === 'site') { a.href = wwwOrigin() + '/'; continue }
  a.addEventListener('click', (e) => { e.preventDefault(); if (world === dom) dom.setMode(mode) })
}
function drawControlPill() {
  document.getElementById('control')?.remove()
  document.getElementById('close-stream').hidden = !currentStream()
  for (const x of streams) world.drawPointer?.(x)
}
function closeWindow(spec) {
  if (spec.kind === 'orb') { removeWindow(spec); return }
  if (spec.kind === 'html') {
    const i = sessionWindows.indexOf(spec); if (i >= 0) sessionWindows.splice(i, 1)
    for (const x of streams.filter((x) => x.embed === spec)) closeStream(x)   // the streams inside its panes
    if (kbField && spec.dom.root.contains(kbField)) endFieldTyping()
    world.remove(spec)
    try { spec.dom.dispose?.() } catch {}
    spec.dom.root.remove()
    removeWindow(spec)
    return
  }
  if (spec.id === 'macapps') { LIVE_WINDOWS.delete('macapps'); if (world.current === spec) world.current = null; applyLive(); relayout(); if (!world.current) world.go(sessionWindows[0] || WINDOWS.find((w) => w.id === 'home')); return }
  if (spec.stream) closeStream(spec)
  else if (spec.kind === 'session') closeSession(spec)
}

function sendPointer(spec, action, extra = {}) {
  spec.player?.touched()
  spec.stream.send('input', { action, x: spec.ptr.x, y: spec.ptr.y, clicks: 1, modifiers: spec.mods || 0, ...extra })
}

// MARK: the worlds that draw the windows — what each may ask of the app

/// What both worlds read and call: the windows, their state, and the app's own actions.
const worldApp = {
  get WINDOWS() { return WINDOWS }, get streams() { return streams }, get sessionWindows() { return sessionWindows },
  isLive: (id) => LIVE_WINDOWS.has(id), RADIUS, PPM, EYE, SETTINGS, COMPONENT, macOf, clientOf, robotsStore,
  sendPointer, get streamKeys() { return streamKeys }, homeState, note: (text) => { liveStatus.textContent = text }, logLine,
  // a window came to the front: the prompt row, the stream's Close, which streams run at full rate
  onChoose: () => { drawControlPill(); drawInputBar(); applyStreamAttention(); repaint() },
  onMode: () => relayout(),
  closeWindow: (spec) => closeWindow(spec),   // the × on a screen (core/screen-close.js)
}
/// …and the immersive room besides: closing, opening, typing into a field with the air keyboard.
const glApp = {
  closeWindow, openWindow: (kind, arg) => openWindow(kind, arg), openMacWindow: (w) => openMacWindow(w), windowContext,
  resizeHtml: (spec, w, h) => resizeHtmlWindow(spec, w, h), relayout, repaint, fail,
  kbField: () => liveField(), fieldKey: (key) => fieldKey(key), promptBox,
}
Object.defineProperties(glApp, Object.getOwnPropertyDescriptors(worldApp))
dom = new DomWorld(worldApp)
world = dom
for (const spec of WINDOWS) if (spec.dom) world.add(spec)

drawState()

applyLive()
// offline-first (offline/): the kept lists and prompts from disk, the outbox's triggers, the update bar
// the bar's build tag (buildtag/, update-chip.md): facecheck / previews / local — the build, tap = check, Reload
const buildTag = startBuildTag({ stamp: BUILD, check: checkVersionNow, log: logLine })
startOffline({ macs, repaint, extOf: (key) => ({ rows: Extensions.homeRows(key), groups: Extensions.homeGroups(key) }),
  openSessions: () => sessionWindows.map((w) => ({ mac: w.macKey || macs.current?.key, session: w.sessionID })),
  onKept: () => { for (const w of sessionWindows) w.dom?.render?.() }, offerUpdate: buildTag.offer })
if (connection) goLive()
installControls()
// the glasses (even/index.js): on these same links; a Mac paired with the code joins the list now (or, the first one,
// the app starts again with it), one forgotten from the pairing sheet leaves it
if (even) even.start(hostFor(macs, {
  onPaired: () => { if (connection?.rooms) { macs.adopt(); repaint() } else location.reload() },
  onForgot: (room) => forgetThisMac(room),
})).catch((e) => logLine('Even: the glasses did not start — ' + (e?.message || e)))



// MARK: standing in it — Snap or Look (core/render-dom.js), the page's own DOM; an immersive session draws the room
// (core/render-gl.js, loaded only then)
{
  const q = new URLSearchParams(location.search)
  let wanted = q.has('look') ? 'look' : q.has('snap') || q.has('scene') || q.has('phone') ? 'snap' : null
  if (!wanted) { try { wanted = localStorage.getItem('cody.view') === 'look' ? 'look' : 'snap' } catch { wanted = 'snap' } }
  const go = WINDOWS.find((w) => w.id === wantedAtStart) || WINDOWS.find((w) => w.id === 'home')
  relayout()
  world.go(go)
  applyLive()
  if (wanted === 'look') world.setMode('look')
}

// A newer build on the server reloads the page (Josh, 09-27: "force a reload when the version is different, maybe
// on first webrtc connect it checks"): the served page's build stamp is compared with this one's on every
// WebRTC connection, when the page comes back into view and once a minute — and the reload waits while
// something is typed in the prompt box.
async function checkForNewBuild(why) {
  if (!BUILD.stamp) return false
  try {
    const html = await (await fetch(location.pathname, { cache: 'no-store' })).text()
    const newer = newerBuild(html, BUILD)
    if (newer) {
      // offline-first: with the service worker in charge the new build is fetched and offered by its bar (offline/)
      if (newerVersion(why, html)) return true
      if (promptBox?.value.trim()) { logLine('a newer build is up (' + newer.stamp + '); reloading once the prompt box is empty'); return true }
      logLine('a newer build is up (' + newer.stamp + ', this is ' + BUILD.stamp + '); reloading — ' + why)
      location.reload()
      return true
    }
  } catch {}
  return false
}
/// The build tag's tap (buildtag/): the worker's own check, then the page's stamp — 'updating' or 'current'.
async function checkVersionNow(why) {
  const r = await checkVersion(why)
  if (r === 'installing' || r === 'registered' || r === 'offer' || r === 'apply') return 'updating'
  return (await checkForNewBuild(why)) ? 'updating' : 'current'
}
addEventListener('visibilitychange', () => { if (!document.hidden) checkForNewBuild('back in view') })
setInterval(() => { if (!document.hidden) checkForNewBuild('a minute passed') }, 60000)

// Errors where a phone can read them: the status line under the scene, since there is no console to open there.
addEventListener('error', (e) => { logLine('error: ' + (e.message || e.error || e) + (e.filename ? ' @' + e.filename.split('/').pop() + ':' + e.lineno : '')); liveStatus.textContent = 'Error: ' + (e.message || e.error || e); live.hidden = false })
addEventListener('unhandledrejection', (e) => { logLine('rejection: ' + (e.reason?.stack || e.reason?.message || e.reason || 'unknown')); liveStatus.textContent = 'Error: ' + (e.reason?.message || e.reason || 'unknown'); live.hidden = false })

// the scene's objects, reachable from a console or a test script (window.cody)
window.cody = { WINDOWS, streams, sessionWindows, enterXR, leaveXR, openSession, closeWindow, homeWin, openWindow, get KINDS() { return windowKinds() }, get world() { return world }, get client() { return client }, openMacWindow, closeStream, get macWindows() { return macWindows }, macs, robots: robotsStore, offline: { keepPrompt, linkUp: offlineLinkUp, remember: rememberMac, prefetch: prefetchMac }, buildTag }

// Tell the page we got this far, so it can stop apologising for a blank rectangle.
document.getElementById('stage').dataset.ready = '1'
